import React, { useState, useEffect } from 'react';
import { apiFetch } from '../services/api_service';
import { API } from '../App';
import { Clock, CheckCircle, AlertCircle, TrendingUp } from 'lucide-react';

const MigrationPhases = () => {
  const [phases, setPhases] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchPhases = async () => {
    const data = await apiFetch(`${API}/phases`);
    if (data?._error) {
      console.error('Error fetching phases:', data.message);
      setLoading(false);
      return;
    }
    setPhases(data);
    setLoading(false);
  };

   
  useEffect(() => {
    fetchPhases();
  }, []);

  const statusColors = {
    completed: { bg: 'bg-green-100', text: 'text-green-800', icon: <CheckCircle className="text-green-600" size={24} /> },
    in_progress: { bg: 'bg-blue-100', text: 'text-blue-800', icon: <Clock className="text-blue-600" size={24} /> },
    not_started: { bg: 'bg-slate-100', text: 'text-slate-800', icon: <AlertCircle className="text-slate-600" size={24} /> },
    delayed: { bg: 'bg-red-100', text: 'text-red-800', icon: <AlertCircle className="text-red-600" size={24} /> }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-96"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div></div>;
  }

  const overallProgress = phases.reduce((sum, p) => sum + p.progress_percentage, 0) / phases.length;

  return (
    <div className="space-y-6" data-testid="migration-phases-page">
      <div>
        <h1 className="text-3xl font-bold text-slate-900 mb-2">AWS Migration Phases</h1>
        <p className="text-slate-600">26-week structured delivery roadmap</p>
      </div>

      {/* Overall Progress */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-800 rounded-lg p-6 text-white">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-2xl font-bold">Overall Migration Progress</h2>
            <p className="text-blue-100">Weeks 1-26 | AWS Cloud Adoption Framework</p>
          </div>
          <TrendingUp size={48} />
        </div>
        <div className="mb-2">
          <div className="flex justify-between text-sm mb-1">
            <span>Progress</span>
            <span className="font-bold">{overallProgress.toFixed(0)}%</span>
          </div>
          <div className="w-full bg-blue-900 rounded-full h-4">
            <div
              className="bg-white h-4 rounded-full transition-all"
              style={{ width: `${overallProgress}%` }}
            ></div>
          </div>
        </div>
        <div className="grid grid-cols-3 gap-4 mt-4 text-center">
          <div>
            <div className="text-3xl font-bold">{phases.filter(p => p.status === 'completed').length}</div>
            <div className="text-sm text-blue-200">Completed</div>
          </div>
          <div>
            <div className="text-3xl font-bold">{phases.filter(p => p.status === 'in_progress').length}</div>
            <div className="text-sm text-blue-200">In Progress</div>
          </div>
          <div>
            <div className="text-3xl font-bold">{phases.filter(p => p.status === 'not_started').length}</div>
            <div className="text-sm text-blue-200">Not Started</div>
          </div>
        </div>
      </div>

      {/* Timeline */}
      <div className="space-y-4">
        {phases.map((phase, index) => {
          const statusStyle = statusColors[phase.status];
          return (
            <div key={phase.id} className="bg-white rounded-lg shadow-lg p-6 hover:shadow-xl transition-shadow">
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0">
                  <div className="w-16 h-16 rounded-full bg-blue-100 flex items-center justify-center">
                    <span className="text-2xl font-bold text-blue-600">{phase.phase_number}</span>
                  </div>
                </div>
                
                <div className="flex-1">
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <h3 className="text-xl font-bold text-slate-900">{phase.name}</h3>
                      <p className="text-sm text-slate-600">Weeks {phase.start_week}-{phase.end_week}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      {statusStyle.icon}
                      <span className={`px-3 py-1 ${statusStyle.bg} ${statusStyle.text} text-xs font-semibold rounded-full`}>
                        {phase.status.replace('_', ' ').toUpperCase()}
                      </span>
                    </div>
                  </div>

                  <p className="text-slate-700 mb-4">{phase.description}</p>

                  {/* Progress Bar */}
                  <div className="mb-4">
                    <div className="flex justify-between text-sm mb-1">
                      <span className="font-medium text-slate-600">Completion</span>
                      <span className="font-bold text-slate-900">{phase.progress_percentage}%</span>
                    </div>
                    <div className="w-full bg-slate-200 rounded-full h-3">
                      <div
                        className="bg-blue-600 h-3 rounded-full transition-all"
                        style={{ width: `${phase.progress_percentage}%` }}
                      ></div>
                    </div>
                  </div>

                  {/* Deliverables */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <div>
                      <span className="text-xs font-semibold text-slate-500 uppercase block mb-2">Key Deliverables</span>
                      <ul className="space-y-1">
                        {phase.key_deliverables.map((deliverable, idx) => (
                          <li key={idx} className="text-sm text-slate-700 flex items-start gap-2">
                            <CheckCircle className="text-blue-600 flex-shrink-0 mt-0.5" size={16} />
                            <span>{deliverable}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div>
                      <span className="text-xs font-semibold text-slate-500 uppercase block mb-2">AWS Services</span>
                      <div className="flex flex-wrap gap-2">
                        {phase.aws_services.map((service, idx) => (
                          <span key={idx} className="px-2 py-1 bg-orange-100 text-orange-700 text-xs rounded">
                            {service}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Dependencies & Risks */}
                  {(phase.dependencies.length > 0 || phase.risks.length > 0) && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4 border-t">
                      {phase.dependencies.length > 0 && (
                        <div>
                          <span className="text-xs font-semibold text-slate-500 uppercase block mb-2">Dependencies</span>
                          <div className="flex flex-wrap gap-1">
                            {phase.dependencies.map((dep, idx) => (
                              <span key={idx} className="px-2 py-1 bg-slate-100 text-slate-700 text-xs rounded">
                                {dep}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                      {phase.risks.length > 0 && (
                        <div>
                          <span className="text-xs font-semibold text-slate-500 uppercase block mb-2">Risks</span>
                          <div className="flex flex-wrap gap-1">
                            {phase.risks.map((risk, idx) => (
                              <span key={idx} className="px-2 py-1 bg-red-100 text-red-700 text-xs rounded">
                                {risk}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default MigrationPhases;